import clsx from 'clsx'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { HouseContext, TravelTypeEnum } from '../../../../../lib/graphql'
import {
  decimalPrice,
  DEFAULT_MINIMUM_VALUE_IN_CENTS,
} from '../../../../../utils'
import { Button, BUTTON_COLOR } from '../../../../atoms'
import { useCurrencyAreaContext } from '../../../../contexts'

interface IHouseSearchResultsCardPrices {
  house: Pick<HouseContext, 'startingFees'>
  numberOfNights: number | undefined
  numberOfDays: number | undefined
  travelType: TravelTypeEnum
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  hasDraftHouse?: boolean
}

const HouseSearchResultsCardPrices = ({
  house,
  numberOfNights,
  numberOfDays,
  travelType,
  onClick,
  hasDraftHouse = false,
}: IHouseSearchResultsCardPrices) => {
  const { currency } = useCurrencyAreaContext()

  return (
    <div
      className={clsx(s.priceContainer, {
        [s.alternativePriceContainer]: !onClick,
      })}
    >
      <div className={s.priceContainerText}>
        {travelType === TravelTypeEnum.DAILY_TRIP ? (
          <>
            <div className={s.overText}>
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.startingFees'
                defaultMessage='A partir de'
              />
            </div>
            <div className={s.price}>
              {decimalPrice(house.startingFees?.dailyPrice, {
                minimumValueInCents: DEFAULT_MINIMUM_VALUE_IN_CENTS,
                currency,
              })}
              /
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.adult'
                defaultMessage='voyageur'
              />
            </div>
            <div className={s.subText}>
              <FormattedMessage
                id='ui.houseSearchResult.card.prices.perDay'
                defaultMessage='Pour {numberOfDays, plural, one {une journée} other {# journées}}'
                values={{ numberOfDays: numberOfDays ?? 1 }}
              />
            </div>
          </>
        ) : travelType === TravelTypeEnum.NIGHTLY_TRIP ? (
          <>
            <div className={s.overText}>
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.startingFees'
                defaultMessage='A partir de'
              />
            </div>
            <div className={s.price}>
              {decimalPrice(house.startingFees?.price, {
                minimumValueInCents: DEFAULT_MINIMUM_VALUE_IN_CENTS,
                currency,
              })}
              /
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.adult'
                defaultMessage='voyageur'
              />
            </div>
            <div className={s.subText}>
              <FormattedMessage
                id='ui.houseSearchResult.card.prices.perNight'
                defaultMessage='Pour {numberOfNights, plural, one {une nuit} other {# nuits}}'
                values={{ numberOfNights: numberOfNights ?? 1 }}
              />
            </div>
          </>
        ) : (
          <>
            <div className={s.overText}>
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.startingFees'
                defaultMessage='A partir de'
              />
            </div>
            <div className={s.price}>
              {decimalPrice(undefined, {
                minimumValueInCents: DEFAULT_MINIMUM_VALUE_IN_CENTS,
                currency,
              })}
              /
              <FormattedMessage
                id='ui.HouseSearchResultsCardPrices.person'
                defaultMessage='personne'
              />
            </div>
            <div className={s.subText}>
              <FormattedMessage
                id='ui.houseSearchResult.card.prices.event'
                defaultMessage='Pour la soirée'
              />
            </div>
          </>
        )}
      </div>
      {onClick && (
        <Button
          color={BUTTON_COLOR.SECONDARY}
          className={s.button}
          onClick={onClick}
          disabled={hasDraftHouse}
          data-testid='get-quotation-button'
        >
          {hasDraftHouse ? (
            <FormattedMessage
              id='ui.HouseSearchResultsCardPrices.draftHouse'
              defaultMessage='Lieu en brouillon'
            />
          ) : (
            <FormattedMessage
              id='ui.HouseSearchResultsCardPrices.getQuote'
              defaultMessage='Obtenir un devis'
            />
          )}
        </Button>
      )}
    </div>
  )
}

export default HouseSearchResultsCardPrices
